import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Hero from './components/Hero';
import CategoryTabs from './components/CategoryTabs';
import MovieGrid from './components/MovieGrid';
import RankingSection from './components/RankingSection';
import ArticleSection from './components/ArticleSection';
import { movies, newMovies, classicMovies } from '@/mocks/movies';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredMovies = useMemo(() => {
    if (activeCategory === 'all') return movies;
    return movies.filter((m) => m.categorySlug === activeCategory);
  }, [activeCategory]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Hero />

        <div className="mx-auto max-w-[1440px] px-4 md:px-6">
          <div className="py-8 md:py-10 space-y-12 md:space-y-16">
            {/* Category Tabs + Filtered Grid */}
            <section>
              <CategoryTabs active={activeCategory} onSelect={setActiveCategory} />
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <div key={movie.id} data-product-shop="">
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 group cursor-pointer" onClick={() => window.REACT_APP_NAVIGATE(`/article/${movie.id}`)}>
                      <img
                        src={movie.poster}
                        alt={movie.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent" />
                      {movie.isNew && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-accent-500 text-background-950 text-xs font-semibold">NEW</span>
                      )}
                      {movie.isHot && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">热映</span>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <i className="ri-star-fill text-accent-500 text-xs" />
                          <span className="text-xs font-semibold text-accent-400">{movie.rating}</span>
                          <span className="text-xs text-foreground-400 ml-2">{movie.year}</span>
                        </div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-50 truncate">{movie.title}</h3>
                        <p className="text-xs text-foreground-400 mt-0.5 truncate">{movie.category} · {movie.duration}</p>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center">
                          <i className="ri-play-fill text-background-50 text-xl ml-0.5" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* New Movies */}
            <MovieGrid title="最新上映" subtitle="最新上线的热门电影，快来看看吧" movies={newMovies} />

            {/* Classic Movies */}
            <MovieGrid title="经典重温" subtitle="时光沉淀的永恒佳作，百看不厌的经典" movies={classicMovies} />

            {/* Ranking */}
            <RankingSection />

            {/* Articles */}
            <ArticleSection />

            {/* FAQ Section */}
            <section className="rounded-xl bg-background-100/60 p-6 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 text-center mb-8">常见问题</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                {[
                  { q: '陌陌影视的电影资源需要付费吗？', a: '不需要！陌陌影视所有电影都是完全免费在线观看的，无需注册，无需付费，随时随地畅享高清画质。' },
                  { q: '电影画质怎么样？支持哪些清晰度？', a: '我们提供高清（720P）至超清（1080P）多种画质选择，根据您的网络情况自动匹配最佳画质，确保流畅不卡顿。' },
                  { q: '可以在手机上观看吗？', a: '当然可以！陌陌影视完美适配手机、平板和电脑端，无论您在哪里，都能享受极致观影体验。' },
                  { q: '电影资源更新频率如何？', a: '我们的片库每日更新，确保您能第一时间看到最新上映的热门大片。新片上线速度快，资源覆盖全面。' },
                ].map((faq, idx) => (
                  <div key={idx} className="p-4 rounded-lg bg-background-200/50">
                    <h3 className="font-heading text-sm font-semibold text-foreground-100 mb-2">{faq.q}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '陌陌影视',
            url: 'https://www.momomovies.com/',
            description: '陌陌影视电影大全提供最新电影、热映大片、经典老片和电影排行榜，高清画质免费在线观看。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.momomovies.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '陌陌影视',
            url: 'https://www.momomovies.com/',
            description: '高清电影免费在线观看平台',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'service@momomovies.com',
              contactType: 'customer service',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '陌陌影视的电影资源需要付费吗？', acceptedAnswer: { '@type': 'Answer', text: '不需要！陌陌影视所有电影都是完全免费在线观看的，无需注册，无需付费，随时随地畅享高清画质。' } },
              { '@type': 'Question', name: '电影画质怎么样？支持哪些清晰度？', acceptedAnswer: { '@type': 'Answer', text: '我们提供高清（720P）至超清（1080P）多种画质选择，根据您的网络情况自动匹配最佳画质，确保流畅不卡顿。' } },
              { '@type': 'Question', name: '可以在手机上观看吗？', acceptedAnswer: { '@type': 'Answer', text: '当然可以！陌陌影视完美适配手机、平板和电脑端，无论您在哪里，都能享受极致观影体验。' } },
              { '@type': 'Question', name: '电影资源更新频率如何？', acceptedAnswer: { '@type': 'Answer', text: '我们的片库每日更新，确保您能第一时间看到最新上映的热门大片。新片上线速度快，资源覆盖全面。' } },
            ],
          }),
        }}
      />
    </>
  );
}